/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GetCollectionPageDataQuery,
  useArchiveCollectionMutation,
  useGetCollectionPageDataQuery,
  usePublishCollectionMutation,
} from '../../../../generated/graphql';
import { emitGAEvent } from '@client/new/services/ga4';
import { trackEvent } from '@services/tracking';
import toast from 'react-hot-toast';
import { BusinessModel, Program } from '@playbook-technologies/common-enums';
import { Price, RawCollection, SalesType } from './domain';
import { isTreeHealthy } from './helpers';
import { checkPublishedProgramHardReqs, validateToPublish } from './validation';
import { useEffect, useState } from 'react';
import { isEmpty, uniq } from 'lodash';
import { Collection, CollectionProgram } from './collection.domain';
import ajax from '@client/components/ajax';
import { Async } from '@client/new/helpers/types';
import { toPublishBundleIds, toWorkoutTiny } from './mappers';
import { COLLECTION_SETTINGS } from '@client/components/collections/collections.settings';
import {
  validateCategory,
  validateProgram,
} from '@client/components/collections/validation';
import { useCustomizations } from '@client/new/hooks/use-customizations';
import { ProgramStateKind } from '@client/new/pages/programs/model/programs.types';

export const toCollectionType = (
  accessType: string,
  type: number,
): SalesType | 'category' => {
  if (accessType === Program.AccessType.Exclusive.value) return 'otp';
  if (type === Program.Type.Collection.value) return 'category';
  if (type === Program.Type.Program.value) return 'subscription';
  return 'subscription';
};

const serializeCollection = (c: any) => {
  c.tags = uniq([...c.tags, ...c.targetArea]);
  return c;
};
const getAdditionalFields = (c: RawCollection) => {
  const additionalFields: any = {};
  if (c.type === 0 && c.type === Program.Type.Program.value) {
    additionalFields.hiddenSections ||= {
      // NOTE: probably better to change on backend (on-read)
      faq: false,
      testimonials: true,
    };
  }
  additionalFields.targetArea = (c.tags || []).filter(
    (x) => x && COLLECTION_SETTINGS.COLLECTION_TARGET_AREA.includes(x),
  );
  return additionalFields;
};

export type SalesPageConfig = {
  id: number;
  programId: number;
  config?: object;
};

type OpenModal = 'success' | undefined;

export type CollectionModel = {
  publishing: boolean;
  publishError?: string;
  publishProgram: () => void;
  archiveProgram: () => void;
  publishAndGoLiveError?: Array<string>;
  collection: Collection;
  originalValues?: GetCollectionPageDataQuery['collection'];
  save: (delta?: Partial<Collection>) => Promise<boolean>;
  price?: Price;
  salesPageConfig?: SalesPageConfig;
  validationTree: any;
  creator?: any;
  programDeepLink?: string;
  validationErrors: {
    errors: Array<string>;
    onClear: () => void;
  };
  openModal: OpenModal;
  closeModal: () => void;
  refetch: () => void;
  onAccessTypeChange: (accessType: string) => void;
  onTypeChange: (type: number) => void;
  onChange: (c: Partial<Collection>) => void;
  publishToPlatformBundle: boolean;
  setPublishToPlatformBundle: (value: boolean) => void;
  hasPlatformBundle: boolean;
};

export const useCollectionModel = ({
  collectionId,
  creatorId,
  derivedState,
}: {
  collectionId: number;
  creatorId: number;
  /**
   * Derived four-state lifecycle of this Program as resolved by the Programs
   * page. Passed as a query param (`programDerivedState`) when navigating
   * from the unified Programs screen. Absent when navigating from other entry
   * points — guard is skipped in that case.
   */
  derivedState?: ProgramStateKind | null;
}): Async<CollectionModel> => {
  const {
    isLoading: isCustomizationsLoading,
    error: customizationsError,
    customizations,
  } = useCustomizations(String(creatorId));
  const isUnifiedProgramsStateUnknown =
    isCustomizationsLoading || !!customizationsError;
  const isUnifiedProgramsEnabled =
    !isCustomizationsLoading &&
    !customizationsError &&
    customizations?.isUnifiedProgramsEnabled === true;

  const [publishCollectionMutation, publishStateRaw] =
    usePublishCollectionMutation();
  const [archiveCollection, archiveStateRaw] = useArchiveCollectionMutation();
  const [, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Array<string>>([]);
  const [openModal, setOpenModal] = useState<OpenModal>(undefined);
  const [tree, setTree] = useState({});
  const [collection, setCollection] = useState<Collection>();
  const [isModified, setIsModified] = useState(false);
  const [publishToPlatformBundle, setPublishToPlatformBundle] = useState(true);

  const { data, loading, error, refetch } = useGetCollectionPageDataQuery({
    variables: {
      collectionId,
      creatorId,
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data?.collection) {
      setCollection({
        ...data?.collection,
        workouts: data.collection.workouts
          ? (toWorkoutTiny(data.collection.workouts as any) as any)
          : [],
        ...getAdditionalFields(data.collection),
      });
    }
  }, [data?.collection]);

  if (loading)
    return {
      type: 'fetching',
    };

  if (error)
    return {
      type: 'fetch-error',
      value: error.message,
    };

  if (!collection || isEmpty(collection))
    return {
      type: 'not-fetched',
    };

  const salesPageConfig = data?.purchasePageConfig || undefined;
  const creator = data?.creator || undefined;

  const save = async (delta: Partial<CollectionProgram> = {}) => {
    const updatedCollection = { ...collection, ...delta };

    setIsSaving(true);
    if (updatedCollection.name) {
      updatedCollection.name = updatedCollection.name.trim();
    }
    if (updatedCollection.description) {
      updatedCollection.description = updatedCollection.description.trim();
    }

    // Client-side save guard: protect the "PUBLISHED Program must satisfy all
    // hard requirements" invariant. Only active for unified-Programs creators
    // navigating from the Programs page (derivedState passed via query param).
    //
    // Diff-aware: a pre-existing violation on the server-persisted Program
    // (inconsistent backend state) is not retro-blocked here — we only block
    // saves that *introduce* a new violation.
    if (derivedState === 'published' && isUnifiedProgramsStateUnknown) {
      setValidationErrors([
        'Unable to verify program publishing requirements. Please try again.',
      ]);
      setIsSaving(false);
      return false;
    }

    if (isUnifiedProgramsEnabled && derivedState === 'published') {
      const persistedData = data?.collection;
      const originalViolations = persistedData
        ? checkPublishedProgramHardReqs(persistedData)
        : [];
      // Use persistedData.type so a forged type-flip in the delta cannot bypass
      // Program hard-req checks by pretending to be a Collection type.
      const candidateForCheck = persistedData
        ? { ...updatedCollection, type: persistedData.type }
        : updatedCollection;
      const candidateViolations =
        checkPublishedProgramHardReqs(candidateForCheck);

      const originalSet = new Set(originalViolations);
      const newViolations = candidateViolations.filter(
        (v) => !originalSet.has(v),
      );

      if (newViolations.length > 0) {
        setValidationErrors(candidateViolations);
        setIsSaving(false);
        return false;
      }
    }

    // TBD when program pages are reworked move validation to model, remove tree, add zod, at the moment keeping for backwards compatibility
    const tree =
      updatedCollection.type === Program.Type.Program.value
        ? validateProgram({ ...updatedCollection } as any, false)
        : validateCategory({ ...updatedCollection } as any, false);
    setTree(tree);
    const isHealthy = isTreeHealthy(tree);

    const programData: any = {
      workoutLimit: null,
    };

    const categoryData: any = {
      duration: [null, 'weeks'],
      contentStructure: null,
      goal: null,
      daysPerWeek: null,
      sessionDuration: null,
    };

    const programPayload = serializeCollection({
      ...updatedCollection,
      ...(updatedCollection.type === Program.Type.Collection.value
        ? categoryData
        : {}),
      ...(updatedCollection.type === Program.Type.Program.value
        ? programData
        : {}),
    });

    if (!isHealthy) {
      setIsSaving(false);
      toast.error('Please fill in required fields');
      return false;
    }

    try {
      await ajax.upsertProgram(programPayload as any);
      toast.success('Saved');
      await emitGAEvent({
        type: programPayload.id ? 'Collection Created' : 'Collection Updated',
        payload: {
          collection_id: programPayload.id,
          influencer_id: programPayload.influencerId,
          collection_type: toCollectionType(
            updatedCollection.accessType as any,
            updatedCollection.type as any,
          ),
        },
      });
      refetch();
      setIsModified(false);
      setValidationErrors([]);
      return true;
    } catch (e) {
      toast.error('Could not save collection.');
      console.log('[CollectionEdit] Error upserting Collection.', e);
      setIsSaving(false);
      return false;
    }
  };

  const onAccessTypeChange = (accessType: string) => {
    !isEmpty(collection) &&
      setCollection((prev: any) => ({
        ...prev,
        accessType,
      }));
  };

  const onTypeChange = (type: number) => {
    collection &&
      setCollection((prev: any) => ({
        ...prev,
        type,
        publishingBehaviour:
          type === Program.Type.Program.value
            ? Program.PublishingBehaviour.Bottom.value
            : Program.PublishingBehaviour.Top.value,
      }));
  };

  const metadata = data?.collection.exclusiveProgramMetadata;

  const price = {
    price: metadata?.originalPrice || metadata?.price || 0,
    discountedPrice:
      metadata?.discountPercent && metadata?.price
        ? metadata?.price
        : undefined,
  };

  const publishProgram = async () => {
    setValidationErrors([]);
    try {
      const mappedErrors = await validateToPublish(collection, price);
      if (!isEmpty(mappedErrors)) {
        setValidationErrors(mappedErrors);
        return;
      }
    } catch (e: any) {
      if (e.errors) setValidationErrors(e.errors.map((x: any) => x.message));
      else setValidationErrors([e.message]);
      return;
    }

    if (isModified) {
      setValidationErrors(['Please save changes before publishing']);
      return;
    }

    const bundleIds =
      data?.creator?.businessModel === BusinessModel.Legacy ||
      collection.accessType === Program.AccessType.Exclusive.value
        ? null
        : toPublishBundleIds(publishToPlatformBundle, data?.bundle);

    publishCollectionMutation({
      variables: { programId: collection.id, onlyPublish: true, bundleIds },
    })
      .then(() => {
        refetch();
        setOpenModal('success');
        emitGAEvent({
          type: 'Collection Published',
          payload: {
            collection_id: collection.id,
            collection_type: toCollectionType(
              collection.accessType,
              collection.type,
            ),
            influencer_id: collection.influencerId,
          },
        });
        trackEvent({
          type: 'Collection Published',
          payload: {
            'Live Status': 'N/A', // TODO
            'Collection Type': 'Program',
            'Product Type':
              collection.accessType === Program.AccessType.Exclusive.value
                ? 'OTP'
                : 'Subscription',
          },
        });
      })
      .catch((err) => {
        console.log('error', err);
        toast.error(err.message);
      });
  };

  const archiveProgram = () => {
    archiveCollection({
      variables: { collectionId: collection.id },
    })
      .then(() => {
        refetch();
      })
      .catch((err) => {
        toast.error(err.message);
      });
  };

  const publishAndGoLiveError: string[] = [];
  const hasPlatformBundle = !isEmpty(
    data?.bundle.filter((b) => b.isPlatformBundle),
  );

  return {
    type: 'fetched',
    value: {
      publishing: publishStateRaw.loading || archiveStateRaw.loading,
      publishError: publishStateRaw?.error?.message,
      publishProgram,
      archiveProgram,
      publishAndGoLiveError,
      collection,
      originalValues: data?.collection,
      price,
      salesPageConfig,
      save,
      validationTree: tree,
      creator,
      programDeepLink:
        collection.exclusiveProgramMetadata?.deeplinkUrl || undefined,
      validationErrors: {
        errors: validationErrors,
        onClear: () => setValidationErrors([]),
      },
      openModal,
      closeModal: () => setOpenModal(undefined),
      refetch: () => refetch(),
      onAccessTypeChange,
      onTypeChange,
      onChange: (c) => {
        setCollection((prev: any) => ({ ...prev, ...c }));
        setIsModified(true);
      },
      publishToPlatformBundle,
      setPublishToPlatformBundle: (value) => setPublishToPlatformBundle(value),
      hasPlatformBundle,
    },
  };
};
